import { useEffect, useRef } from "react";

const VERTS = [
  [-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1],
  [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1],
];
const EDGES = [
  [0, 1], [1, 2], [2, 3], [3, 0],
  [4, 5], [5, 6], [6, 7], [7, 4],
  [0, 4], [1, 5], [2, 6], [3, 7],
];

const HeroCanvas = () => {
  const ref = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    let raf;
    let t = 0;

    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const orbiters = Array.from({ length: 16 }, (_, i) => ({
      phase: (i / 16) * Math.PI * 2,
      radius: 1.7 + (i % 3) * 0.35,
      tilt: (i % 5) * 0.35,
    }));

    const project = ([x, y, z], rx, ry, scale, cx, cy) => {
      const x1 = x * Math.cos(ry) + z * Math.sin(ry);
      const z1 = -x * Math.sin(ry) + z * Math.cos(ry);
      const y1 = y * Math.cos(rx) - z1 * Math.sin(rx);
      const z2 = y * Math.sin(rx) + z1 * Math.cos(rx);
      const p = 3.4 / (3.4 - z2);
      return [cx + x1 * scale * p, cy + y1 * scale * p, z2];
    };

    const draw = () => {
      t += 0.0045;
      ctx.clearRect(0, 0, w, h);
      const cx = w / 2;
      const cy = h / 2;
      const scale = Math.min(w, h) * 0.24;
      const rx = t * 0.7 + mouse.current.y * 0.7;
      const ry = t + mouse.current.x * 0.9;

      const pts = VERTS.map((v) => project(v, rx, ry, scale, cx, cy));

      ctx.strokeStyle = "rgba(0, 85, 255, 0.22)";
      ctx.lineWidth = 1;
      EDGES.forEach(([a, b]) => {
        ctx.beginPath();
        ctx.moveTo(pts[a][0], pts[a][1]);
        ctx.lineTo(pts[b][0], pts[b][1]);
        ctx.stroke();
      });

      const inner = VERTS.map(([x, y, z]) =>
        project([x * 0.45, y * 0.45, z * 0.45], -rx * 1.4, -ry * 1.3, scale, cx, cy)
      );
      ctx.strokeStyle = "rgba(10, 13, 20, 0.35)";
      EDGES.forEach(([a, b]) => {
        ctx.beginPath();
        ctx.moveTo(inner[a][0], inner[a][1]);
        ctx.lineTo(inner[b][0], inner[b][1]);
        ctx.stroke();
      });

      pts.forEach(([px, py]) => {
        ctx.fillStyle = "#0055FF";
        ctx.fillRect(px - 3, py - 3, 6, 6);
      });
      inner.forEach(([px, py]) => {
        ctx.fillStyle = "#0A0D14";
        ctx.fillRect(px - 1.5, py - 1.5, 3, 3);
      });

      orbiters.forEach((o) => {
        const a = t * 1.6 + o.phase;
        const ox = Math.cos(a) * o.radius;
        const oz = Math.sin(a) * o.radius;
        const oy = Math.sin(a * 0.8 + o.tilt) * 0.5;
        const [px, py] = project([ox, oy, oz], rx * 0.3, ry * 0.3, scale, cx, cy);
        ctx.fillStyle = "rgba(0, 85, 255, 0.7)";
        ctx.beginPath();
        ctx.arc(px, py, 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "rgba(0, 85, 255, 0.12)";
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(cx, cy);
        ctx.stroke();
      });

      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div
      data-testid="hero-incubator-canvas"
      className="relative w-full h-full"
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mouse.current = {
          x: (e.clientX - r.left) / r.width - 0.5,
          y: (e.clientY - r.top) / r.height - 0.5,
        };
      }}
    >
      <canvas ref={ref} className="w-full h-full block" />
    </div>
  );
};

export default HeroCanvas;
